import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  Download, Eye, FileText, Loader2, Paperclip, Pencil, RefreshCw, Search, Trash2, X,
} from 'lucide-react'
import {
  fetchDocuments, uploadDocument, updateDocument, deleteDocument, reprocessDocument, searchDocuments,
  viewDocumentPdf, downloadDocumentFile, fetchSummary, fetchFirms, FirmDocument, FirmSummary, DocumentUpdate,
} from '../../lib/api/api'
import { useAuth } from '../../contexts/AuthContext'
import { useAttachments } from '../../contexts/AttachmentContext'
import { formatDate } from '../../shared/utils/format'
import DragDropUpload from '../../shared/components/DragDropUpload'
import SummaryModal from '../../shared/components/SummaryModal'
import DocumentTypeSelector from './DocumentTypeSelector'

type EditForm = { title: string; description: string }

// A row of the list: a full FirmDocument, or one of the leaner records the
// hybrid search returns. Search results carry no can_edit/can_delete/
// can_reprocess: those are looked up against the main list (permissionsOf),
// an unknown document (arrived only via search) staying read-only.
type Row = FirmDocument | (Record<string, any> & { id: string | number; title: string | null })

export default function FirmDocumentsPage() {
  const { t } = useTranslation()
  const { user, isSuperAdmin } = useAuth()
  const { attachDocument, detachDocument, isDocumentAttached } = useAttachments()
  const [searchParams] = useSearchParams()
  const highlightId = searchParams.get('highlight')

  // The super admin belongs to no firm: they must pick one before the list
  // or an upload means anything (a member's own firm is implicit otherwise,
  // and the server 400s an upload with no firm_id — see firm_scope.py).
  const needsFirmPicker = isSuperAdmin && !user?.firm
  const [firms, setFirms] = useState<FirmSummary[]>([])
  const [selectedFirmId, setSelectedFirmId] = useState('')

  const [documents, setDocuments] = useState<FirmDocument[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const [uploadTypeCode, setUploadTypeCode] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Array<Record<string, any>> | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  const [editingDoc, setEditingDoc] = useState<FirmDocument | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({ title: '', description: '' })
  const [isSaving, setIsSaving] = useState(false)

  const [viewerDocId, setViewerDocId] = useState<string | number | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [isLoadingPdf, setIsLoadingPdf] = useState(false)

  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [summaryContent, setSummaryContent] = useState<string | null>(null)
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false)

  const highlightRef = useRef<HTMLLIElement | null>(null)

  useEffect(() => {
    if (!needsFirmPicker) return
    fetchFirms().then((res) => setFirms(res.data.firms)).catch(() => {})
  }, [needsFirmPicker])

  useEffect(() => {
    let active = true
    if (needsFirmPicker && !selectedFirmId) {
      setDocuments([])
      return
    }
    setIsLoading(true)
    setError(null)
    const params = needsFirmPicker ? { firm_id: selectedFirmId } : {}
    fetchDocuments(params)
      .then((res) => { if (active) setDocuments(res.data.documents) })
      .catch(() => { if (active) setError(t('documents.errors.loadFailed')) })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [needsFirmPicker, selectedFirmId, t])

  useEffect(() => {
    if (highlightId && highlightRef.current && typeof highlightRef.current.scrollIntoView === 'function') {
      highlightRef.current.scrollIntoView({ block: 'center' })
    }
  }, [highlightId, documents])

  // Permissions come from the server with the list; a search result may be
  // missing from it (paginated out) and then stays read-only.
  const permissionsOf = (id: string | number): FirmDocument | undefined =>
    documents.find((d) => String(d.id) === String(id))

  const handleFilesSelected = async (files: File[]) => {
    if (files.length === 0) return
    if (needsFirmPicker && !selectedFirmId) return // the drop zone is disabled in this state too
    setIsUploading(true)
    setError(null)
    setSuccessMessage(null)
    let accepted = 0
    const failed: string[] = []
    await Promise.all(files.map(async (file) => {
      try {
        const form = new FormData()
        form.append('file', file)
        if (uploadTypeCode) form.append('document_type_code', uploadTypeCode)
        if (needsFirmPicker) form.append('firm_id', selectedFirmId)
        await uploadDocument(form)
        accepted++
      } catch {
        failed.push(file.name)
      }
    }))
    if (accepted > 0) setSuccessMessage(t('documents.upload.accepted', { count: accepted }))
    if (failed.length > 0) setError(t('documents.upload.errorFor', { files: failed.join(', ') }))
    setIsUploading(false)
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    setIsSearching(true)
    setError(null)
    try {
      const res = await searchDocuments(searchQuery)
      setSearchResults(res.data.documents)
    } catch {
      setError(t('documents.errors.searchFailed'))
    } finally {
      setIsSearching(false)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults(null)
  }

  const handleAttachToggle = (row: Row) => {
    const id = String(row.id)
    if (isDocumentAttached(id, 'firm_document')) {
      detachDocument(id, 'firm_document')
    } else {
      attachDocument({
        id,
        title: row.title || '',
        type: 'firm_document',
        doc_type: (row as FirmDocument).document_type_code || 'firm_document',
        file_name: (row as FirmDocument).file_name || row.title || '',
      })
    }
  }

  const handleDelete = async (doc: FirmDocument) => {
    if (!window.confirm(t('documents.list.confirmDelete', { title: doc.title }))) return
    try {
      await deleteDocument(doc.id)
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id))
    } catch {
      setError(t('documents.errors.deleteFailed'))
    }
  }

  const openEdit = (doc: FirmDocument) => {
    setEditingDoc(doc)
    setEditForm({ title: doc.title || '', description: doc.description || '' })
  }

  const handleSaveEdit = async () => {
    if (!editingDoc) return
    setIsSaving(true)
    try {
      const body: DocumentUpdate = { title: editForm.title, description: editForm.description }
      const res = await updateDocument(editingDoc.id, body)
      setDocuments((prev) => prev.map((d) => (d.id === editingDoc.id ? res.data : d)))
      setEditingDoc(null)
    } catch {
      setError(t('documents.errors.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleReprocess = async (doc: FirmDocument) => {
    if (!window.confirm(t('documents.list.confirmReprocess'))) return
    try {
      await reprocessDocument(doc.id)
      setSuccessMessage(t('documents.list.reprocessAccepted'))
    } catch {
      setError(t('documents.errors.reprocessFailed'))
    }
  }

  const handleView = async (doc: FirmDocument) => {
    setViewerDocId(doc.id)
    setPdfUrl(null)
    setPdfError(null)
    setIsLoadingPdf(true)
    try {
      const url = await viewDocumentPdf(doc.id)
      setPdfUrl(url)
    } catch {
      setPdfError(t('documents.viewer.loadFailed'))
    } finally {
      setIsLoadingPdf(false)
    }
  }

  // Revokes the previous blob URL whenever it's replaced or the page
  // unmounts (closing the viewer sets pdfUrl to null, which runs this same
  // cleanup once with the URL that was showing — never a double revoke).
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
    }
  }, [pdfUrl])

  const handleCloseViewer = () => {
    setViewerDocId(null)
    setPdfUrl(null)
    setPdfError(null)
  }

  const handleGenerateSummary = async () => {
    setIsGeneratingSummary(true)
    setShowSummaryModal(true)
    setSummaryContent(null)
    try {
      // the super admin summarises the firm they picked (the server 400s without it)
      const res = needsFirmPicker
        ? await fetchSummary('documents', { firm_id: selectedFirmId })
        : await fetchSummary('documents')
      setSummaryContent(res.data.summary)
    } catch {
      setError(t('documents.errors.summaryFailed'))
      setShowSummaryModal(false)
    } finally {
      setIsGeneratingSummary(false)
    }
  }

  const rows = (searchResults ?? documents) as Row[]
  const uploadDisabled = isUploading || (needsFirmPicker && !selectedFirmId)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-gray-100">{t('documents.title')}</h1>
        {documents.length > 0 && !searchResults && (
          <button
            type="button"
            onClick={handleGenerateSummary}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm"
          >
            <FileText size={16} />
            {t('documents.summary.cta')}
          </button>
        )}
      </div>

      {needsFirmPicker && (
        <div className="flex items-center gap-2">
          <label htmlFor="documents-firm-picker" className="text-sm text-gray-600 dark:text-gray-400">
            {t('documents.firmPicker.label')}
          </label>
          <select
            id="documents-firm-picker"
            value={selectedFirmId}
            onChange={(e) => setSelectedFirmId(e.target.value)}
            className="border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
          >
            <option value="">{t('documents.firmPicker.placeholder')}</option>
            {firms.map((firm) => (
              <option key={firm.id} value={firm.id}>{firm.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="p-4 border rounded-lg bg-white dark:bg-gray-800 space-y-3">
        <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('documents.upload.title')}</h2>
        <div>
          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
            {t('documents.upload.typeLabel')}
          </label>
          <DocumentTypeSelector value={uploadTypeCode} onChange={setUploadTypeCode} disabled={uploadDisabled} />
        </div>
        <DragDropUpload
          onFilesSelected={handleFilesSelected}
          acceptedFileTypes={['.pdf', '.docx', '.doc', '.xml']}
          multiple
          disabled={uploadDisabled}
          label={t('documents.upload.dropLabel')}
          customFormatDescription={<p>{t('documents.upload.formats')}</p>}
        />
        {needsFirmPicker && !selectedFirmId && (
          <p className="text-sm text-amber-600">{t('documents.upload.selectFirmHint')}</p>
        )}
        {isUploading && (
          <div className="flex items-center gap-2 text-sm text-primary-700">
            <Loader2 className="animate-spin" size={16} />
            {t('common.loading')}
          </div>
        )}
      </div>

      {successMessage && <p className="text-green-700 text-sm">{successMessage}</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
            placeholder={t('documents.search.placeholder')}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
          />
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={isSearching || !searchQuery.trim()}
          className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm disabled:opacity-50"
        >
          {isSearching ? <Loader2 className="animate-spin" size={16} /> : t('common.search')}
        </button>
        {searchResults && (
          <button type="button" onClick={clearSearch} className="text-sm text-gray-500 hover:text-gray-700">
            <X size={16} />
          </button>
        )}
      </div>

      {needsFirmPicker && !selectedFirmId ? (
        <p className="text-gray-500 text-sm">{t('documents.firmPicker.empty')}</p>
      ) : isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-gray-400" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-gray-500 text-center py-8">{t('documents.list.noResults')}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => {
            const perms = searchResults ? permissionsOf(row.id) : (row as FirmDocument)
            const canEdit = !!perms?.can_edit
            const canDelete = !!perms?.can_delete
            const canReprocess = !!perms?.can_reprocess
            const fileAvailable = row.file_available !== false
            const attached = isDocumentAttached(String(row.id), 'firm_document')
            const isHighlighted = highlightId != null && String(row.id) === highlightId
            return (
              <li
                key={row.id}
                data-testid={`document-${row.id}`}
                data-highlighted={String(isHighlighted)}
                ref={(el) => { if (isHighlighted) highlightRef.current = el }}
                className={`p-4 rounded-lg border transition-colors ${
                  isHighlighted ? 'border-primary-400 bg-primary-50 dark:bg-primary-950'
                    : attached ? 'border-primary-200 bg-primary-50/40 dark:bg-primary-950/40'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100 break-all">{row.title}</h3>
                      {attached && (
                        <span className="text-xs bg-primary-700 text-white px-2 py-0.5 rounded-full">
                          {t('common.attached')}
                        </span>
                      )}
                      {!fileAvailable && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          {t('documents.list.fileUnavailable')}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                      {row.document_type_name && <span>{row.document_type_name}</span>}
                      {row.uploaded_by && <span>{t('documents.list.uploadedBy', { name: row.uploaded_by })}</span>}
                      {row.created_at && <span>{formatDate(row.created_at)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleAttachToggle(row)}
                      title={attached ? t('common.detach') : t('common.attach')}
                      aria-label={attached ? t('common.detach') : t('common.attach')}
                      className={`p-2 rounded-full ${attached ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                    >
                      <Paperclip size={18} />
                    </button>
                    {fileAvailable && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleView(row as FirmDocument)}
                          title={t('documents.list.viewPdf')}
                          aria-label={t('documents.list.viewPdf')}
                          className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadDocumentFile(row.id)}
                          title={t('common.download')}
                          aria-label={t('common.download')}
                          className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        >
                          <Download size={18} />
                        </button>
                      </>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => openEdit(row as FirmDocument)}
                        title={t('common.edit')}
                        aria-label={t('common.edit')}
                        className="p-2 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                      >
                        <Pencil size={18} />
                      </button>
                    )}
                    {canReprocess && fileAvailable && (
                      <button
                        type="button"
                        onClick={() => handleReprocess(row as FirmDocument)}
                        title={t('documents.list.reprocess')}
                        aria-label={t('documents.list.reprocess')}
                        className="p-2 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                      >
                        <RefreshCw size={18} />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(row as FirmDocument)}
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                        className="p-2 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {editingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{t('common.edit')}</h3>
              <button type="button" onClick={() => setEditingDoc(null)} className="text-gray-500 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                aria-label={t('common.edit')}
                className="w-full border rounded-md px-3 py-1.5"
              />
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={4}
                className="w-full border rounded-md px-3 py-1.5"
              />
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button type="button" onClick={() => setEditingDoc(null)} className="px-3 py-1.5 rounded-md border text-sm">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewerDocId != null && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500/75" onClick={handleCloseViewer} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">{t('documents.viewer.title')}</h3>
                  <button
                    type="button"
                    onClick={handleCloseViewer}
                    aria-label={t('documents.viewer.close')}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className="w-full h-[80vh] bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {isLoadingPdf && <Loader2 className="animate-spin" size={32} />}
                  {pdfError && <p className="text-gray-600">{pdfError}</p>}
                  {pdfUrl && !isLoadingPdf && !pdfError && (
                    <iframe src={pdfUrl} title={t('documents.viewer.iframeTitle')} className="w-full h-full" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <SummaryModal
        isOpen={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        title={t('documents.summary.title')}
        summary={summaryContent}
        isLoading={isGeneratingSummary}
        documentTitle={t('documents.title')}
      />
    </div>
  )
}
